import { useCallback, useState } from 'react'
import { isEmpty } from 'ramda'

import useCallbackRef from 'app/common/hooks/useCallbackRef'
import { EncashmentsStatusType } from 'app/dataTypes/reports/data'
import { StopPropagation } from 'app/common/ui-components/table'
import SimpleDate from 'app/common/classes/simple-date'
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link/plain-button-as-link'
import {
  EncashmentsAction,
  EncashmentsStatusAction,
} from 'app/dataTypes/reports/data/finances/encashments/action'

import { PrevioslyUnacceptedInfo } from './PrevioslyUnacceptedInfo'
import { ActionButtonWithConfirm } from './ActionButtonWithConfirm'
import { ActionButtonWithPrompt } from './ActionButtonWithPrompt'
import styles from './Action.module.scss'

type Props = {
  status: EncashmentsStatusType | null
  date: SimpleDate
  salePointId: string
  salePointTitle: string
  schoolTitle: string
  onChangeStatus: (params: EncashmentsStatusAction) => Promise<boolean>
  pendingDates: Array<SimpleDate>
}

export const Action = ({
  status,
  date,
  salePointId,
  salePointTitle,
  schoolTitle,
  onChangeStatus,
  pendingDates,
}: Props) => {
  const hasPrevioslyUnaccepted = !isEmpty(pendingDates)
  const [disabled, setDisabled] = useState<boolean>(false)

  const onChangeStatusRef = useCallbackRef(onChangeStatus)
  const handleAccept = useCallback(async () => {
    setDisabled(true)
    const res = await onChangeStatusRef.current?.({
      salePointId,
      date,
      action: EncashmentsAction.Accept,
    })
    setDisabled(false)
    return !!res
  }, [salePointId, date, onChangeStatusRef])

  const handleReject = useCallback(
    async (comment: string) => {
      setDisabled(true)
      const res = await onChangeStatusRef.current?.({
        salePointId,
        date,
        action: EncashmentsAction.Reject,
        comment,
      })
      setDisabled(false)
      return !!res
    },
    [salePointId, date, onChangeStatusRef],
  )

  return (
    <StopPropagation className={styles.root}>
      {status === EncashmentsStatusType.WaitingForCash && hasPrevioslyUnaccepted && (
        <PrevioslyUnacceptedInfo dates={pendingDates} />
      )}

      {status === EncashmentsStatusType.WaitingForCash && !hasPrevioslyUnaccepted && (
        <>
          <ActionButtonWithConfirm
            title="Подтвердить"
            confirmText={`Подтвердить принятие наличных для точки продажи — ${salePointTitle} (${schoolTitle})?`}
            onConfirm={handleAccept}
          />
          <ActionButtonWithPrompt
            title="Отказать"
            confirmText={`Введите причину отказа для точки продажи — ${salePointTitle} (${schoolTitle})`}
            onConfirm={handleReject}
          >
            {(toggle) => (
              <PlainButtonAsLink
                onClick={toggle}
                disabled={disabled}
                linkClassName={styles.rejectBtn}
                type="button"
              >
                {disabled ? 'обработка...' : 'Отказать'}
              </PlainButtonAsLink>
            )}
          </ActionButtonWithPrompt>
        </>
      )}
    </StopPropagation>
  )
}
